import {
  observable,
  computed,
  action,
  useStrict,
  runInAction,
  autorun,
  isObservable,
  isObservableArray,
  isObservableMap
} from "mobx";
import BaseStore from "./BaseStore";
import weixinJsSdk from "../utils/weixinJsSdk";
import semver from "semver";
import utils from "../utils/utils";

useStrict(true);

// https://www.w3cschool.cn/jsref/dom-obj-audio.html
// https://testalivideo.foschool.cn/uploads/audios/2017/0731/20170731160256401.mp3

const useEmbedAudioDom = (() => {
  const iOSVersion = utils.iOSVersion();

  // iOS 小于10.4版本的, 渲染audio标签, 否则无法播放
  return !!(iOSVersion && semver.lt(iOSVersion, "10.4.0"));
})();

class StoreAudioPlayer extends BaseStore {
  constructor() {
    super();
    this.useEmbedAudio = useEmbedAudioDom;
  }

  _player = null;
  _loadError = false;
  _playerDom = null;
  @observable
  paused = true;
  @observable
  duration = 0;
  @observable
  currentTime = 0;
  @observable
  src = "";
  @observable
  bufferedTime = 0;
  @observable
  useEmbedAudio = false;

  @computed
  get player() {
    if (!this._player) {
      this._player = this.createPlayer();
    }
    return this._player;
  }

  updatePlayer(player) {
    if (this._player) {
      this.removeHandlers(this._player);
    }

    this._player = player;
    this.addHandlers(player);
  }

  @action.bound
  createPlayer(dom) {
    let audioElement = dom;
    if (!audioElement) {
      audioElement = document.createElement("audio");
    }

    audioElement.preload = "auto";
    this.removeHandlers(audioElement);
    this.addHandlers(audioElement);
    // document.body.appendChild(audioElement); //把它添加到页面中
    return audioElement;
  }

  @action.bound
  setPlayerDom(_playerDom) {
    if (!_playerDom) {
      return;
    }
    this._player = this.createPlayer(_playerDom);
  }

  @action.bound
  doPlay(src) {
    let shouldReplay = false;
    if (src && this.src !== src) {
      shouldReplay = true;
      this.src = src;
      this.player.src = this.src;
    }

    const index = this.src.lastIndexOf(".");
    const pattern = this.src.slice(index + 1);

    if (!this.player.canPlayType(`audio/${pattern}`)) {
      utils.toast.fail(`无法播放${pattern}格式的音频`);
      return;
    } else {
      console.log(`可以播放${pattern}格式的音频`);
    }

    if (!this.useEmbedAudio || shouldReplay) {
      this.player.play();
    }
  }

  @action.bound
  play(src = "") {
    // 微信下要求jssdk准备完毕后, 才可以播放
    weixinJsSdk.ready().then(() => this.doPlay(src));
  }

  @action.bound
  pause() {
    this.paused = true;
    this.player.pause();
  }

  @action.bound
  stop() {
    this.player.stop();
  }

  removeHandlers(audioElement) {
    audioElement.removeEventListener(
      "durationchange",
      this.handleDurationChange
    );
    audioElement.removeEventListener("canplay", this.handleCanPlay);
    audioElement.removeEventListener("progress", this.handleProgress);
    audioElement.removeEventListener("timeupdate", this.handleTimeUpdate);
    audioElement.removeEventListener("playing", this.handlePlaying);
    audioElement.removeEventListener("pause", this.handlePause);
    audioElement.removeEventListener("ended", this.handleEnded);
  }
  addHandlers(audioElement) {
    audioElement.addEventListener("durationchange", this.handleDurationChange);
    audioElement.addEventListener("canplay", this.handleCanPlay);
    audioElement.addEventListener("progress", this.handleProgress);
    audioElement.addEventListener("timeupdate", this.handleTimeUpdate);
    audioElement.addEventListener("playing", this.handlePlaying);
    audioElement.addEventListener("pause", this.handlePause);
    audioElement.addEventListener("ended", this.handleEnded);
    audioElement.addEventListener("error", this.handleError);
  }

  @action.bound
  handlePause() {
    this.paused = true;
  }

  @action.bound
  handleEnded() {
    this.paused = true;
    this.currentTime = 0;
  }

  @action.bound
  handlePlaying() {
    this.paused = false;
  }

  @action.bound
  handleProgress() {
    // step1 下载进度更新
    this.duration = this.player.duration;
    const buffered = this.player.buffered;
    const length = buffered.length;
    const bufferedTime = length > 0 ? buffered.end(length - 1) : 0;

    if (bufferedTime !== this.bufferedTime) {
      this.bufferedTime = bufferedTime;
    }
  }

  @action.bound
  handleDurationChange() {
    // step2 总时长更新
    this.duration = this.player.duration;
  }

  @action.bound
  handleCanPlay() {
    // step3 可播放
    this.duration = this.player.duration;

    // if (!this.paused) {
    //   this.player.play();
    // }
  }

  @action.bound
  handleTimeUpdate() {
    // step4 loop 播放进度更新
    this.currentTime = this.player.currentTime;
  }

  @action.bound
  seek(time) {
    this.player.currentTime = time;
  }

  @action.bound
  handleError() {
    this._loadError = true;
    utils.toast.fail("音频资源不存在");
  }
}

export default new StoreAudioPlayer();



// WEBPACK FOOTER //
// ./src/store/storeAudioPlayer.js